import React, { useState, useEffect } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import {
  LayoutDashboard, ShoppingCart, Users, FileText, Receipt, LogOut, Menu,
  TrendingUp, DollarSign, Package, Calendar, ChevronRight, Plus,
  Loader2, Search, Trash2, Printer, Download
} from "lucide-react";
import { Button } from "../../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Badge } from "../../components/ui/badge";
import { Sheet, SheetContent, SheetTrigger } from "../../components/ui/sheet";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../../components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../components/ui/table";
import { useAuth } from "../../App";
import DeleteAccountSection from "../../components/DeleteAccountSection";
import apiClient from "../../lib/api";
import LanguageSwitcher from "../../components/LanguageSwitcher";
import { toastApiError } from "../../lib/toastApiError";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_97109381-3560-4331-a0e4-28136ead7f0e/artifacts/z6ijff3c_logo1.png";

// Sidebar Navigation for Seller
const SellerSidebar = ({ className = "", onNavigate }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { logout, user } = useAuth();
  const { t } = useTranslation("nav");
  const { t: tc } = useTranslation("common");

  const isManager = user?.role === "pharmacy";

  const navItems = [
    { icon: LayoutDashboard, labelKey: "seller.dashboard", path: "/seller" },
    { icon: ShoppingCart, labelKey: "seller.pos", path: "/seller/pos" },
    { icon: Receipt, labelKey: "seller.salesHistory", path: "/seller/sales" },
    { icon: FileText, labelKey: "seller.invoices", path: "/seller/invoices" },
  ];

  return (
    <div className={`flex flex-col h-full ${className}`}>
      <div className="p-6">
        <Link to="/">
          <img src={LOGO_URL} alt="OMU" className="h-16 w-auto" />
        </Link>
        <Badge className="mt-2 bg-omu-blue-100 text-omu-blue-700">
          {isManager ? t("seller.managerBadge") : t("seller.sellerBadge")}
        </Badge>
      </div>

      <nav className="flex-1 px-4 space-y-1">
        {/* Back to main dashboard for managers */}
        {isManager && (
          <Link
            to="/dashboard"
            className="flex items-center gap-3 px-4 py-3 rounded-xl transition-colors text-omu-blue-600 hover:bg-omu-blue-50 mb-2 border border-omu-blue-200"
            data-testid="nav-back-dashboard"
          >
            <ChevronRight className="h-5 w-5 rotate-180" />
            <span className="font-medium">{t("seller.backToPharmacy")}</span>
          </Link>
        )}
        
        {navItems.map((item) => (
          <Link
            key={item.path}
            to={item.path}
            onClick={onNavigate}
            className={`flex items-center gap-3 px-4 py-3 rounded-xl transition-colors ${
              location.pathname === item.path || location.pathname.startsWith(`${item.path}/`)
                ? "bg-omu-blue-500 text-white"
                : "text-slate-600 hover:bg-slate-100"
            }`}
            data-testid={`nav-${item.labelKey.split(".").pop()}`}
          >
            <item.icon className="h-5 w-5" />
            <span className="font-medium">{t(item.labelKey)}</span>
          </Link>
        ))}
      </nav>

      <div className="p-4 mt-auto space-y-3">
        <LanguageSwitcher className="w-full justify-center" compact />
        <Button
          variant="ghost"
          className="w-full justify-start text-slate-600 hover:text-red-500"
          onClick={() => {
            logout();
            navigate("/");
          }}
          data-testid="logout-btn"
        >
          <LogOut className="h-5 w-5 mr-3" />
          {tc("logout")}
        </Button>
      </div>
    </div>
  );
};

// Dashboard Layout for Seller
export const SellerDashboardLayout = ({ children, title }) => {
  const { user } = useAuth();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  return (
    <div className="min-h-[100dvh] bg-slate-50">
      {/* Desktop Sidebar */}
      <aside className="hidden lg:fixed lg:inset-y-0 lg:flex lg:w-64 lg:flex-col bg-white border-r border-slate-200">
        <SellerSidebar />
      </aside>

      {/* Mobile Header */}
      <header className="lg:hidden sticky top-0 z-50 bg-white border-b border-slate-200">
        <div className="flex items-center justify-between h-16 px-4">
          <Link to="/">
            <img src={LOGO_URL} alt="OMU" className="h-10 w-auto" />
          </Link>
          <Sheet open={mobileMenuOpen} onOpenChange={setMobileMenuOpen}>
            <SheetTrigger asChild>
              <Button variant="ghost" size="icon">
                <Menu className="h-6 w-6" />
              </Button>
            </SheetTrigger>
            <SheetContent side="left" className="p-0 w-64">
              <SellerSidebar onNavigate={() => setMobileMenuOpen(false)} />
            </SheetContent>
          </Sheet>
        </div>
      </header>

      {/* Main Content */}
      <main className="lg:pl-64">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 pb-12">
          {title && (
            <h1 className="font-heading text-2xl font-bold text-slate-900 mb-6">
              {title}
            </h1>
          )}
          {children}
        </div>
      </main>
    </div>
  );
};

// Main Seller Dashboard Page
const SellerDashboard = () => {
  const { t } = useTranslation("seller");
  const { t: tn } = useTranslation("nav");
  const { i18n } = useTranslation();
  const { user } = useAuth();
  const navigate = useNavigate();
  const dateLocale = i18n.language === "en" ? "en-US" : "fr-FR";
  const [stats, setStats] = useState(null);
  const [recentSales, setRecentSales] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Check if user is a pharmacy manager (has full access to profits)
  const isManager = user?.role === "pharmacy";

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      const [statsRes, salesRes] = await Promise.all([
        apiClient.get("/pos/stats"),
        apiClient.get("/pos/sales?period=all&limit=5")
      ]);
      setStats(statsRes.data);
      // Handle both direct array or {sales: []} response
      const salesData = Array.isArray(salesRes.data) ? salesRes.data : (salesRes.data.sales || []);
      // Limit to 5 most recent
      setRecentSales(salesData.slice(0, 5));
    } catch (error) {
      console.error("Error fetching dashboard data:", error);
      toastApiError(error);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('fr-CD', {
      style: 'currency',
      currency: 'CDF',
      minimumFractionDigits: 0
    }).format(amount || 0);
  };

  if (loading) {
    return (
      <SellerDashboardLayout title={tn("seller.dashboard")}>
        <div className="flex items-center justify-center py-20">
          <Loader2 className="h-12 w-12 animate-spin text-omu-blue-500" />
        </div>
      </SellerDashboardLayout>
    );
  }

  return (
    <SellerDashboardLayout title={t("dashboard.sellerTitle")}>
      <div className="mb-8">
        <h2 className="text-xl text-slate-600">
          {t("dashboard.welcome")} <span className="font-semibold text-slate-900">{user?.name || t("dashboard.defaultName")}</span>
        </h2>
        <p className="text-sm text-slate-500 mt-1">
          {new Date().toLocaleDateString(dateLocale, { weekday: "long", year: "numeric", month: "long", day: "numeric" })}
        </p>
      </div>

      {/* Stats Grid */}
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
        {/* Today's Sales */}
        <Card className="border-0 shadow-soft" data-testid="stat-today-sales">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">{t("dashboard.todaySales")}</p>
                <p className="text-3xl font-bold text-slate-900 mt-1">
                  {stats?.today?.sales || 0}
                </p>
                <p className="text-lg font-semibold text-green-600 mt-1">
                  {formatCurrency(stats?.today?.revenue)}
                </p>
              </div>
              <div className="w-12 h-12 bg-omu-blue-50 rounded-xl flex items-center justify-center">
                <ShoppingCart className="h-6 w-6 text-omu-blue-500" />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Weekly Stats */}
        <Card className="border-0 shadow-soft" data-testid="stat-week-sales">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">{t("dashboard.thisWeek")}</p>
                <p className="text-3xl font-bold text-slate-900 mt-1">
                  {stats?.week?.sales || 0}
                </p>
                <p className="text-lg font-semibold text-green-600 mt-1">
                  {formatCurrency(stats?.week?.revenue)}
                </p>
              </div>
              <div className="w-12 h-12 bg-purple-50 rounded-xl flex items-center justify-center">
                <Calendar className="h-6 w-6 text-purple-500" />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Monthly Stats */}
        <Card className="border-0 shadow-soft" data-testid="stat-month-sales">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">{t("dashboard.thisMonth")}</p>
                <p className="text-3xl font-bold text-slate-900 mt-1">
                  {stats?.month?.sales || 0}
                </p>
                <p className="text-lg font-semibold text-green-600 mt-1">
                  {formatCurrency(stats?.month?.revenue)}
                </p>
              </div>
              <div className="w-12 h-12 bg-omu-blue-50 rounded-xl flex items-center justify-center">
                <TrendingUp className="h-6 w-6 text-omu-blue-500" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Profits Section - Only for Manager */}
      {isManager && (
        <div className="grid sm:grid-cols-3 gap-6 mb-8">
          <Card className="border-0 shadow-soft bg-gradient-to-br from-green-50 to-emerald-50" data-testid="stat-today-profit">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-green-700">{t("dashboard.todayProfit")}</p>
                  <p className="text-2xl font-bold text-green-600 mt-1">
                    {formatCurrency(stats?.today?.profit || 0)}
                  </p>
                </div>
                <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                  <DollarSign className="h-5 w-5 text-green-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="border-0 shadow-soft bg-gradient-to-br from-green-50 to-emerald-50" data-testid="stat-week-profit">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-green-700">{t("dashboard.weekProfit")}</p>
                  <p className="text-2xl font-bold text-green-600 mt-1">
                    {formatCurrency(stats?.week?.profit || 0)}
                  </p>
                </div>
                <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                  <DollarSign className="h-5 w-5 text-green-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="border-0 shadow-soft bg-gradient-to-br from-green-50 to-emerald-50" data-testid="stat-month-profit">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-green-700">{t("dashboard.monthProfit")}</p>
                  <p className="text-2xl font-bold text-green-600 mt-1">
                    {formatCurrency(stats?.month?.profit || 0)}
                  </p>
                </div>
                <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                  <DollarSign className="h-5 w-5 text-green-600" />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Quick Action - New Sale */}
      <Card 
        className="mb-8 border-0 bg-gradient-to-r from-omu-blue-500 to-omu-blue-600 text-white cursor-pointer hover:shadow-lg transition-shadow"
        onClick={() => navigate("/seller/pos")}
        data-testid="quick-new-sale"
      >
        <CardContent className="p-6">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="font-heading text-xl font-semibold">
                {t("dashboard.newSale")}
              </h3>
              <p className="opacity-90 mt-1">
                {t("dashboard.newSaleDesc")}
              </p>
            </div>
            <div className="w-14 h-14 bg-white/20 rounded-full flex items-center justify-center">
              <Plus className="h-8 w-8" />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Recent Sales */}
      <Card className="border-0 shadow-soft">
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="text-lg">{t("dashboard.recentSales")}</CardTitle>
          <Button 
            variant="outline" 
            size="sm"
            onClick={() => navigate("/seller/sales")}
            className="rounded-full"
          >
            {t("actions.viewAll")}
          </Button>
        </CardHeader>
        <CardContent>
          {recentSales.length === 0 ? (
            <p className="text-center text-slate-500 py-8">{t("dashboard.noSales")}</p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>{t("dashboard.colInvoice")}</TableHead>
                  <TableHead>{t("dashboard.colClient")}</TableHead>
                  <TableHead>{t("dashboard.colItems")}</TableHead>
                  <TableHead className="text-right">{t("dashboard.colTotal")}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {recentSales.map((sale) => (
                  <TableRow key={sale.id} className="cursor-pointer hover:bg-slate-50" onClick={() => navigate(`/seller/invoices?sale=${sale.id}`)}>
                    <TableCell className="font-medium">{sale.invoice_number}</TableCell>
                    <TableCell>{sale.client_name || t("dashboard.anonymousClient")}</TableCell>
                    <TableCell>{t("dashboard.itemsCount", { count: sale.items?.length || 0 })}</TableCell>
                    <TableCell className="text-right font-semibold">{formatCurrency(sale.total)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {user?.role === "seller" && (
        <div className="mt-6">
          <DeleteAccountSection />
        </div>
      )}
    </SellerDashboardLayout>
  );
};

export default SellerDashboard;
